export type Team = 'offense' | 'defense';
export type PlayerAction = 'idle' | 'run' | 'catch' | 'gather' | 'shoot' | 'contest' | 'stunt' | 'recover';

export interface RenderPlayer {
  id: string;
  team: Team;
  x: number;
  y: number;
  vx?: number;
  vy?: number;
  facingX?: number;
  facingY?: number;
  hasBall?: boolean;
  targetable?: boolean;
  action?: PlayerAction;
  actionProgress?: number;
  ready?: boolean;
  threatened?: boolean;
  callingForBall?: boolean;
  helpState?: 'home' | 'stunting' | 'recovering';
  dimmed?: boolean;
  featuredHelper?: boolean;
}

export interface RenderBall {
  x: number;
  y: number;
  z?: number;
  visible?: boolean;
  targetX?: number;
  targetY?: number;
}

export interface ShotTrail {
  fromX: number;
  fromY: number;
  progress: number;
  made?: boolean;
  blocked?: boolean;
}

export interface CourtFrame {
  players: RenderPlayer[];
  ball: RenderBall;
  shot?: ShotTrail;
  live: boolean;
  danger?: boolean;
  flash?: 'make' | 'miss' | 'block' | 'horn';
  time: number;
}

interface Point { x: number; y: number }

const COLORS = {
  ink: '#242251', court: '#34316f', courtDark: '#29275d', paint: '#22b8a7', paintDark: '#159584',
  paper: '#fff6dc', orange: '#ff7548', red: '#e64c58', lime: '#d7ef74', offense: '#ff7548', defense: '#b7e875',
};

export class CourtRenderer {
  private context: CanvasRenderingContext2D;
  private width = 720;
  private height = 960;
  private dpr = 1;

  constructor(private canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D unavailable');
    this.context = context;
    this.resize();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.width = Math.max(320, rect.width || 360);
    this.height = Math.max(420, rect.height || 560);
    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
    this.context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.context.imageSmoothingEnabled = true;
  }

  render(frame: CourtFrame): void {
    const ctx = this.context;
    ctx.save();
    ctx.clearRect(0, 0, this.width, this.height);
    this.drawCourt(frame.time);
    this.drawShotTrail(frame.shot);

    const emphasis = (player: RenderPlayer) => player.callingForBall
      ? 4
      : player.featuredHelper
        ? 3
        : player.helpState === 'stunting'
          ? 2
          : player.helpState === 'recovering'
            ? 1
            : 0;
    const sorted = [...frame.players].sort((a, b) => emphasis(a) - emphasis(b) || a.y - b.y);
    for (const player of sorted) this.drawPlayer(player, frame.time, frame.live);
    if (frame.ball.visible !== false) this.drawBall(frame.ball);
    if (frame.shot?.made && frame.shot.progress > .58) this.drawMakeBurst(frame.shot.progress);
    this.drawAtmosphere(frame);
    ctx.restore();
  }

  hitTest(clientX: number, clientY: number, players: RenderPlayer[], targetableOnly = true): string | undefined {
    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    let best: { id: string; distance: number } | undefined;
    for (const player of players) {
      if (targetableOnly && !player.targetable && !player.hasBall) continue;
      const point = this.project(player.x, player.y);
      const radius = this.playerScale(player.y) * 1.15;
      const distance = Math.hypot(x - point.x, y - point.y);
      if (distance <= Math.max(31, radius) && (!best || distance < best.distance)) best = { id: player.id, distance };
    }
    return best?.id;
  }

  private project(x: number, y: number, z = 0): Point {
    const top = 58;
    const bottom = this.height - 8;
    const progress = clamp(y, 0, 1);
    const width = lerp(this.width * .58, this.width * 1.04, progress);
    return {
      x: this.width / 2 + x * width * .48,
      y: lerp(top, bottom, progress) - z * this.height * .13,
    };
  }

  private playerScale(y: number): number { return lerp(29, 48, clamp(y, 0, 1)); }

  private drawCourt(time: number): void {
    const ctx = this.context;
    const topLeft = this.project(-1.08, 0);
    const topRight = this.project(1.08, 0);
    const bottomRight = this.project(1.08, 1);
    const bottomLeft = this.project(-1.08, 1);

    ctx.fillStyle = COLORS.courtDark;
    ctx.fillRect(0, 0, this.width, this.height);
    const gradient = ctx.createLinearGradient(0, 0, 0, this.height);
    gradient.addColorStop(0, COLORS.court);
    gradient.addColorStop(1, '#262452');
    ctx.fillStyle = gradient;
    path(ctx, [topLeft, topRight, bottomRight, bottomLeft]);
    ctx.fill();

    this.fillWorldPolygon([[-.39, 0], [.39, 0], [.43, .54], [-.43, .54]], COLORS.paintDark);
    this.fillWorldPolygon([[-.32, 0], [.32, 0], [.35, .48], [-.35, .48]], COLORS.paint);

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(255,246,220,.86)';
    ctx.lineWidth = 2.2;
    this.strokeWorldLine([[-1.03, 0], [1.03, 0], [1.03, 1], [-1.03, 1], [-1.03, 0]]);
    this.strokeWorldLine([[-.35, 0], [-.35, .48], [.35, .48], [.35, 0]]);
    this.strokeWorldCurve((t) => {
      const angle = Math.PI * (.16 + .68 * t);
      return [Math.cos(angle) * .91, .09 + Math.sin(angle) * .72] as [number, number];
    });
    this.strokeWorldCurve((t) => {
      const angle = Math.PI * 2 * t;
      return [Math.cos(angle) * .24, .48 + Math.sin(angle) * .14] as [number, number];
    });
    this.strokeWorldLine([[-1.03, .94], [1.03, .94]]);

    for (let i = 0; i < 12; i += 1) {
      const y = .08 + i * .077;
      const p = this.project(((i * 37) % 10) / 10 * 1.8 - .9, y);
      ctx.fillStyle = `rgba(255,246,220,${.015 + ((i + Math.floor(time)) % 4) * .008})`;
      ctx.beginPath(); ctx.arc(p.x, p.y, 8 + (i % 3) * 4, 0, Math.PI * 2); ctx.fill();
    }
    this.drawHoop();
  }

  private drawHoop(): void {
    const ctx = this.context;
    const base = this.project(0, .055, .02);
    const board = this.project(0, .015, .18);
    ctx.save();
    ctx.strokeStyle = COLORS.ink;
    ctx.lineWidth = 7;
    ctx.beginPath(); ctx.moveTo(board.x, board.y - 14); ctx.lineTo(board.x, base.y + 24); ctx.stroke();
    ctx.fillStyle = COLORS.paper;
    ctx.strokeStyle = COLORS.ink;
    ctx.lineWidth = 4;
    ctx.fillRect(board.x - 48, board.y - 25, 96, 47);
    ctx.strokeRect(board.x - 48, board.y - 25, 96, 47);
    ctx.strokeRect(board.x - 14, board.y - 3, 28, 20);
    ctx.strokeStyle = COLORS.orange;
    ctx.lineWidth = 6;
    ctx.beginPath(); ctx.ellipse(base.x, base.y, 31, 9, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = 'rgba(255,246,220,.78)';
    ctx.lineWidth = 2;
    for (let i = -3; i <= 3; i += 1) {
      ctx.beginPath(); ctx.moveTo(base.x + i * 8, base.y + 5); ctx.lineTo(base.x + i * 4, base.y + 37); ctx.stroke();
    }
    ctx.beginPath(); ctx.moveTo(base.x - 25, base.y + 18); ctx.lineTo(base.x + 25, base.y + 18); ctx.stroke();
    ctx.restore();
  }

  private drawPlayer(player: RenderPlayer, time: number, live: boolean): void {
    const ctx = this.context;
    const point = this.project(player.x, player.y);
    const scale = this.playerScale(player.y);
    const moving = Math.hypot(player.vx ?? 0, player.vy ?? 0);
    const runBob = live && moving > .02 ? Math.sin(time * 11 + hash(player.id)) * 2.2 : 0;
    const callHop = player.callingForBall ? -Math.abs(Math.sin(time * 6 + hash(player.id))) * 5 : 0;
    const bob = runBob + callHop;
    const action = player.action ?? 'idle';
    const progress = player.actionProgress ?? 0;
    const facing = Math.sign(player.facingX ?? player.vx ?? (player.team === 'offense' ? 1 : -1)) || 1;
    const body = player.team === 'offense' ? COLORS.offense : COLORS.defense;
    const trim = player.team === 'offense' ? COLORS.paper : COLORS.ink;

    ctx.save();
    ctx.translate(point.x, point.y + bob);
    if (player.dimmed) ctx.globalAlpha = .08;

    if (player.helpState === 'stunting' || player.helpState === 'recovering') {
      const trailStrength = player.featuredHelper ? 1 : player.helpState === 'stunting' ? .72 : .38;
      const trailDirection = Math.sign(player.vx ?? facing) || facing;
      ctx.save();
      ctx.strokeStyle = player.helpState === 'stunting'
        ? `rgba(255,246,220,${.72 * trailStrength})`
        : `rgba(215,239,116,${.58 * trailStrength})`;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      for (let streak = 0; streak < 3; streak += 1) {
        ctx.beginPath();
        ctx.moveTo(-trailDirection * scale * (.42 + streak * .08), scale * (-.3 + streak * .2));
        ctx.lineTo(-trailDirection * scale * (1.22 + streak * .18), scale * (-.24 + streak * .2));
        ctx.stroke();
      }
      ctx.globalAlpha = player.featuredHelper ? .46 : player.helpState === 'stunting' ? .12 : .05;
      ctx.fillStyle = COLORS.defense;
      ctx.beginPath();
      ctx.ellipse(-trailDirection * scale * .62, -scale * .08, scale * .3, scale * .5, -.15 * trailDirection, 0, Math.PI * 2);
      ctx.fill();
      if (player.featuredHelper && player.helpState === 'recovering') {
        ctx.beginPath();
        ctx.ellipse(-trailDirection * scale * 1.08, -scale * .05, scale * .25, scale * .43, -.18 * trailDirection, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      ctx.rotate(trailDirection * (player.helpState === 'stunting' ? -.14 : .08));

      if (player.featuredHelper) {
        ctx.save();
        ctx.strokeStyle = COLORS.paper;
        ctx.lineWidth = 9;
        ctx.beginPath(); ctx.ellipse(0, -scale * .08, scale * .58, scale * .92, -.12 * trailDirection, 0, Math.PI * 2); ctx.stroke();
        ctx.strokeStyle = COLORS.lime;
        ctx.lineWidth = 4;
        ctx.beginPath(); ctx.ellipse(0, -scale * .08, scale * .66, scale, -.12 * trailDirection, 0, Math.PI * 2); ctx.stroke();
        if (player.helpState === 'recovering') {
          ctx.strokeStyle = COLORS.paper;
          ctx.lineWidth = 8;
          ctx.beginPath();
          ctx.arc(0, -scale * .08, scale * .96, Math.PI * .05, Math.PI * 1.15, true);
          ctx.stroke();
          ctx.fillStyle = COLORS.lime;
          ctx.beginPath();
          ctx.moveTo(-trailDirection * scale * .78, -scale * .62);
          ctx.lineTo(-trailDirection * scale * 1.06, -scale * .48);
          ctx.lineTo(-trailDirection * scale * .76, -scale * .35);
          ctx.closePath(); ctx.fill();
        }
        ctx.restore();
      }
    }

    ctx.fillStyle = 'rgba(15,14,48,.28)';
    ctx.beginPath(); ctx.ellipse(0, scale * .48, scale * .72, scale * .2, 0, 0, Math.PI * 2); ctx.fill();

    if (player.targetable || player.hasBall) {
      const pulse = 1 + Math.sin(time * 5 + hash(player.id)) * .06;
      ctx.strokeStyle = player.threatened
        ? COLORS.red
        : player.hasBall || player.callingForBall
          ? COLORS.lime
          : 'rgba(255,246,220,.85)';
      ctx.lineWidth = player.hasBall || player.callingForBall ? 6 : 3;
      ctx.setLineDash(player.hasBall || player.callingForBall ? [] : [7, 6]);
      ctx.beginPath(); ctx.ellipse(0, scale * .33, scale * .82 * pulse, scale * .36 * pulse, 0, 0, Math.PI * 2); ctx.stroke();
      if (player.callingForBall) {
        ctx.fillStyle = 'rgba(215,239,116,.16)';
        ctx.fill();
      }
      ctx.setLineDash([]);
    }

    if (player.callingForBall) {
      const pulse = 1 + Math.sin(time * 6 + hash(player.id)) * .04;
      ctx.strokeStyle = COLORS.paper;
      ctx.lineWidth = 8;
      ctx.beginPath(); ctx.ellipse(0, scale * .34, scale * 1.02 * pulse, scale * .46 * pulse, 0, 0, Math.PI * 2); ctx.stroke();
      ctx.strokeStyle = COLORS.lime;
      ctx.lineWidth = 4;
      ctx.beginPath(); ctx.ellipse(0, scale * .34, scale * 1.18 * pulse, scale * .55 * pulse, 0, 0, Math.PI * 2); ctx.stroke();
    }

    const crouch = action === 'gather' ? Math.sin(progress * Math.PI) * 8 : 0;
    const rise = action === 'shoot' ? progress * 16 : 0;
    const contest = action === 'contest' ? clamp(progress, 0, 1) : 0;
    ctx.translate(0, crouch - rise);

    ctx.strokeStyle = COLORS.ink;
    ctx.lineWidth = Math.max(5, scale * .14);
    ctx.lineCap = 'round';
    const legSpread = moving > .02 ? Math.sin(time * 9 + hash(player.id)) * scale * .28 : scale * .18;
    ctx.beginPath(); ctx.moveTo(-scale * .13, scale * .13); ctx.lineTo(-legSpread, scale * .63); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(scale * .13, scale * .13); ctx.lineTo(legSpread, scale * .63); ctx.stroke();
    ctx.strokeStyle = COLORS.paper; ctx.lineWidth = Math.max(3, scale * .07);
    ctx.beginPath(); ctx.moveTo(-legSpread - scale * .12, scale * .65); ctx.lineTo(-legSpread + scale * .1, scale * .65); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(legSpread - scale * .1, scale * .65); ctx.lineTo(legSpread + scale * .13, scale * .65); ctx.stroke();

    ctx.fillStyle = body; ctx.strokeStyle = COLORS.ink; ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(-scale * .38, -scale * .32); ctx.quadraticCurveTo(0, -scale * .48, scale * .38, -scale * .32);
    ctx.lineTo(scale * .27, scale * .28); ctx.quadraticCurveTo(0, scale * .42, -scale * .27, scale * .28); ctx.closePath();
    ctx.fill(); ctx.stroke();
    ctx.strokeStyle = trim; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(-scale * .2, -scale * .32); ctx.quadraticCurveTo(0, -scale * .16, scale * .2, -scale * .32); ctx.stroke();

    const armRaise = action === 'gather'
      ? Math.min(1, progress * 1.18)
      : action === 'shoot' || player.callingForBall
        ? 1
        : action === 'contest' || action === 'stunt'
          ? Math.max(contest, action === 'stunt' ? .72 : 0)
          : 0;
    const armLines = player.callingForBall
      ? [
        [-scale * .29, -scale * .2, -facing * scale * .5, -scale * .94],
        [scale * .29, -scale * .2, facing * scale * .5, -scale * .94],
      ]
      : action === 'stunt'
        ? [
          [-scale * .29, -scale * .2, facing * scale * .72, -scale * .36],
          [scale * .29, -scale * .2, facing * scale * .9, -scale * .72],
        ]
        : [
          [-scale * .29, -scale * .2, -facing * scale * (.5 + armRaise * .1), -scale * (.06 + armRaise * .6)],
          [scale * .29, -scale * .2, facing * scale * (.52 + armRaise * .17), -scale * (.02 + armRaise * .75)],
        ];
    ctx.strokeStyle = COLORS.ink; ctx.lineWidth = Math.max(5, scale * .13);
    for (const [startX, startY, endX, endY] of armLines) {
      ctx.beginPath(); ctx.moveTo(startX, startY); ctx.lineTo(endX, endY); ctx.stroke();
    }
    ctx.strokeStyle = body; ctx.lineWidth = Math.max(2, scale * .055);
    for (const [startX, startY, endX, endY] of armLines) {
      ctx.beginPath(); ctx.moveTo(startX, startY); ctx.lineTo(endX, endY); ctx.stroke();
    }

    ctx.fillStyle = COLORS.paper; ctx.strokeStyle = COLORS.ink; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(facing * scale * .05, -scale * .61, scale * .22, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = COLORS.ink;
    ctx.beginPath(); ctx.arc(facing * scale * .14, -scale * .63, 2.4, 0, Math.PI * 2); ctx.fill();

    if (player.ready) {
      ctx.strokeStyle = COLORS.lime;
      ctx.lineWidth = 4;
      const handX = facing * scale * .69;
      const handY = -scale * .77;
      ctx.beginPath();
      for (let ray = 0; ray < 8; ray += 1) {
        const angle = ray * Math.PI / 4;
        ctx.moveTo(handX + Math.cos(angle) * 9, handY + Math.sin(angle) * 9);
        ctx.lineTo(handX + Math.cos(angle) * 16, handY + Math.sin(angle) * 16);
      }
      ctx.stroke();
    }

    if (player.callingForBall) {
      const pulse = .5 + .5 * Math.sin(time * 7 + hash(player.id));
      for (const handSide of [-1, 1]) {
        const handX = handSide * facing * scale * .5;
        const handY = -scale * .94;
        ctx.fillStyle = COLORS.paper;
        ctx.strokeStyle = COLORS.ink;
        ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(handX, handY, 8, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.strokeStyle = COLORS.lime;
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.arc(handX, handY, 16 + pulse * 6, Math.PI * 1.05, Math.PI * 1.95);
        ctx.stroke();
      }
      ctx.strokeStyle = COLORS.lime;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(-scale * .12, -scale * 1.25);
      ctx.lineTo(0, -scale * (1.4 + pulse * .08));
      ctx.lineTo(scale * .12, -scale * 1.25);
      ctx.stroke();

      // The abandoned assignment literally calls for the ball. Anchoring the
      // shout to the illustrated player makes the extra-pass read physical,
      // not a detached strategy label.
      const bubbleY = -scale * 1.92;
      const bubbleWidth = scale * 1.36;
      const bubbleHeight = scale * .62;
      ctx.fillStyle = COLORS.lime;
      ctx.strokeStyle = COLORS.paper;
      ctx.lineWidth = 9;
      ctx.beginPath();
      ctx.roundRect(-bubbleWidth / 2, bubbleY - bubbleHeight / 2, bubbleWidth, bubbleHeight, 10);
      ctx.fill();
      ctx.stroke();
      ctx.strokeStyle = COLORS.ink;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-scale * .08, bubbleY + bubbleHeight / 2 - 1);
      ctx.lineTo(0, bubbleY + bubbleHeight / 2 + scale * .22);
      ctx.lineTo(scale * .1, bubbleY + bubbleHeight / 2 - 1);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.strokeStyle = COLORS.paper;
      ctx.lineWidth = 5;
      for (const side of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(side * bubbleWidth * .42, bubbleY - bubbleHeight * .2);
        ctx.lineTo(side * bubbleWidth * .62, bubbleY - bubbleHeight * .42);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(side * bubbleWidth * .45, bubbleY + bubbleHeight * .06);
        ctx.lineTo(side * bubbleWidth * .7, bubbleY + bubbleHeight * .02);
        ctx.stroke();
      }
      ctx.fillStyle = COLORS.ink;
      ctx.font = `900 ${Math.max(17, scale * .43)}px "Barlow Condensed", Impact, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('HEY!', 0, bubbleY + 1);
    }

    if (action === 'contest' && contest > .35) {
      const handX = facing * scale * (.52 + contest * .2);
      const handY = -scale * (.02 + contest * .82);
      ctx.fillStyle = contest > .72 ? COLORS.red : COLORS.paper;
      ctx.strokeStyle = COLORS.ink;
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(handX, handY, 6 + contest * 3, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = 'rgba(255,246,220,.75)';
      ctx.lineWidth = 2;
      for (let streak = 0; streak < 3; streak += 1) {
        ctx.beginPath();
        ctx.moveTo(handX - facing * (14 + streak * 5), handY + 7 + streak * 5);
        ctx.lineTo(handX - facing * (30 + streak * 6), handY + 10 + streak * 5);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  private drawBall(ball: RenderBall): void {
    const ctx = this.context;
    const point = this.project(ball.x, ball.y, ball.z ?? 0);
    const radius = lerp(7, 12, clamp(ball.y, 0, 1)) * (1 + (ball.z ?? 0) * .22);
    ctx.save(); ctx.translate(point.x, point.y);
    if (ball.targetX !== undefined && ball.targetY !== undefined) {
      const target = this.project(ball.targetX, ball.targetY, ball.z ?? 0);
      const dx = target.x - point.x;
      const dy = target.y - point.y;
      const magnitude = Math.hypot(dx, dy) || 1;
      const ux = dx / magnitude;
      const uy = dy / magnitude;
      ctx.strokeStyle = 'rgba(255,246,220,.94)';
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      for (let streak = 0; streak < 3; streak += 1) {
        const side = (streak - 1) * 6;
        ctx.beginPath();
        ctx.moveTo(-ux * (16 + streak * 7) - uy * side, -uy * (16 + streak * 7) + ux * side);
        ctx.lineTo(-ux * (58 + streak * 10) - uy * side, -uy * (58 + streak * 10) + ux * side);
        ctx.stroke();
      }
      for (const distance of [28, 48]) {
        ctx.globalAlpha = distance === 28 ? .38 : .18;
        ctx.fillStyle = COLORS.orange;
        ctx.strokeStyle = COLORS.paper;
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(-ux * distance, -uy * distance, radius * .72, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
    ctx.fillStyle = COLORS.orange; ctx.strokeStyle = COLORS.ink; ctx.lineWidth = 2.3;
    ctx.beginPath(); ctx.arc(0, 0, radius, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-radius, 0); ctx.lineTo(radius, 0); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(0, 0, radius * .42, radius, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }

  private drawShotTrail(shot?: ShotTrail): void {
    if (!shot) return;
    const ctx = this.context;
    const start = this.project(shot.fromX, shot.fromY, .12);
    const missSide = shot.fromX <= 0 ? -.16 : .16;
    const end = shot.made
      ? this.project(0, .055, .11)
      : shot.blocked
        ? this.project(shot.fromX * .58 - missSide, Math.max(.18, shot.fromY - .18), .03)
        : this.project(missSide, .13, .015);
    const t = ease(clamp(shot.progress, 0, 1));
    const control = {
      x: lerp(start.x, end.x, shot.blocked ? .35 : .55),
      y: Math.min(start.y, end.y) - this.height * (shot.blocked ? .09 : .23),
    };
    ctx.save();
    ctx.strokeStyle = shot.blocked ? COLORS.red : 'rgba(255,246,220,.6)';
    ctx.lineWidth = 3;
    ctx.setLineDash([7, 8]);
    ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.quadraticCurveTo(control.x, control.y, end.x, end.y); ctx.stroke();
    ctx.setLineDash([]);
    const point = quadratic(start, control, end, t);
    const targetY = shot.made ? .055 : shot.blocked ? Math.max(.18, shot.fromY - .18) : .13;
    const z = Math.sin(Math.PI * t) * (shot.blocked ? .08 : .22);
    this.drawBall({ x: this.unprojectX(point.x, lerp(shot.fromY, targetY, t)), y: lerp(shot.fromY, targetY, t), z, visible: true });
    ctx.restore();
  }

  private drawAtmosphere(frame: CourtFrame): void {
    const ctx = this.context;
    if (frame.danger) {
      ctx.fillStyle = `rgba(230,76,88,${.05 + Math.sin(frame.time * 14) * .025})`;
      ctx.fillRect(0, 0, this.width, this.height);
    }
    if (frame.flash) {
      const color = frame.flash === 'make' ? '215,239,116' : frame.flash === 'horn' ? '230,76,88' : '255,246,220';
      ctx.fillStyle = `rgba(${color},.13)`; ctx.fillRect(0, 0, this.width, this.height);
    }
  }

  private drawMakeBurst(progress: number): void {
    const ctx = this.context;
    const hoop = this.project(0, .055, .08);
    const strength = Math.sin(clamp((progress - .58) / .42, 0, 1) * Math.PI);
    ctx.save();
    ctx.translate(hoop.x, hoop.y + 8);
    ctx.strokeStyle = COLORS.lime;
    ctx.lineWidth = 4;
    for (let ray = 0; ray < 12; ray += 1) {
      const angle = ray * Math.PI / 6;
      const inner = 31 + strength * 7;
      const outer = 47 + strength * 18;
      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner * .55);
      ctx.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer * .55);
      ctx.stroke();
    }
    ctx.strokeStyle = COLORS.paper;
    ctx.lineWidth = 3;
    for (let net = -3; net <= 3; net += 1) {
      ctx.beginPath();
      ctx.moveTo(net * 8, 0);
      ctx.lineTo(net * 4, 35 + strength * 20);
      ctx.stroke();
    }
    ctx.restore();
  }

  private fillWorldPolygon(points: Array<[number, number]>, fill: string): void {
    const ctx = this.context; ctx.fillStyle = fill; path(ctx, points.map(([x, y]) => this.project(x, y))); ctx.fill();
  }

  private strokeWorldLine(points: Array<[number, number]>): void {
    path(this.context, points.map(([x, y]) => this.project(x, y))); this.context.stroke();
  }

  private strokeWorldCurve(fn: (t: number) => [number, number]): void {
    const ctx = this.context; ctx.beginPath();
    for (let i = 0; i <= 36; i += 1) {
      const [x, y] = fn(i / 36); const p = this.project(x, y);
      if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
  }

  private unprojectX(screenX: number, y: number): number {
    const width = lerp(this.width * .58, this.width * 1.04, clamp(y, 0, 1));
    return (screenX - this.width / 2) / (width * .48);
  }
}

function path(ctx: CanvasRenderingContext2D, points: Point[]): void {
  ctx.beginPath(); points.forEach((point, index) => index ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y)); ctx.closePath();
}
function quadratic(a: Point, b: Point, c: Point, t: number): Point { const q = 1 - t; return { x: q * q * a.x + 2 * q * t * b.x + t * t * c.x, y: q * q * a.y + 2 * q * t * b.y + t * t * c.y }; }
function clamp(value: number, min: number, max: number): number { return Math.max(min, Math.min(max, value)); }
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function ease(t: number): number { return 1 - (1 - t) * (1 - t); }
function hash(value: string): number { let result = 0; for (let i = 0; i < value.length; i += 1) result = (result * 31 + value.charCodeAt(i)) | 0; return result; }
